// @flow

import actionCreators from "@modules/reporting/actionCreator";
import { all, call, put, takeEvery } from "redux-saga/effects";
import type { Action } from "redux-action";
import {
  GetEmployeeDetails,
  GetEmployeeRiskSummary,
  GetAbsenceDurationsSummary,
} from "@modules/reporting/constants/actions";
import http from "@http";
import config from "@config";

type EmployeeRequest = $Exact<{
  clientId: number,
  employeeId: number,
}>;

export function* onGetEmployeeDetails({ payload }: Action<EmployeeRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.employeeId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}`,
    );

    yield put(actionCreators.GetEmployeeDetailsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetEmployeeDetailsError(e.response));
  }
}

export function* onGetAbsenceDurationsSummary({
  payload,
}: Action<
  $Exact<{
    clientId: number,
    term: ["short", "medium", "long"],
    employeeId: number,
  }>,
>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.term || !payload.employeeId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.post,
      `${config.dataForecastsEndpointBaseUrl}data-forecast/${payload.clientId}/${payload.employeeId}/employee-absence-duration`,
      {
        term: payload.term,
      },
    );

    yield put(actionCreators.GetAbsenceDurationsSummarySuccess(response.data.durationForecast));
  } catch (e) {
    yield put(actionCreators.GetEmployeeRiskSummaryError(e));
  }
}
export function* onGetEmployeeRiskSummary({
  payload,
}: Action<
  $Exact<{
    clientId: number,
    period: number,
    employeeId: number,
  }>,
>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.period || !payload.employeeId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.post,
      `${config.dataForecastsEndpointBaseUrl}data-forecast/${payload.clientId}/${payload.employeeId}/employee-risk-profile`,
      {
        period: payload.period,
      },
    );
    yield put(actionCreators.GetEmployeeRiskSummarySuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetEmployeeRiskSummaryError(e));
  }
}

export function* EmployeeDetailsSaga(): Generator<*, void, void> {
  yield all([takeEvery(GetEmployeeDetails, onGetEmployeeDetails)]);
  yield all([takeEvery(GetEmployeeRiskSummary, onGetEmployeeRiskSummary)]);
  yield all([takeEvery(GetAbsenceDurationsSummary, onGetAbsenceDurationsSummary)]);
}

export default EmployeeDetailsSaga;
